import React, { useEffect } from "react";
import UploadDocStyles from "./UploadDoc.module.scss";

import { Dispatch, Fragment, SetStateAction, useState } from "react";
import { FileUploadRequestBody } from "../../common/common.model";
import {
  DASHBOARD_ENTITIES,
  FILE_UPLOAD_ERRORS,
  UploadDocConfig,
} from "../../common/constants";
import Button from "../Button/Button";

import CloseIcon from "../../assets/cross_dark.svg";
import BrowseFileSvg from "../../assets/browse_files.svg";
import WarningSvg from "../../assets/warning.svg";
import useDrivePicker from "react-google-drive-picker";
import { useAppDispatch, useAppSelector } from "../../hooks/store";
import { ChooseFromDriveBtn, UploadBtn } from "../Buttons/Buttons";
import {
  uploadFileForClauseCheckThunk,
  uploadFileForContractDraftingThunk,
  uploadFileForContractReviewThunk,
  uploadFileForRfxGeneratorThunk,
  uploadFilesForRfxEvaluationThunk,
  uploadMutliDocFromBotThunk,
  setModalOpen,
  CONTRACT_DRAFTING_ACTIONS,
  CONTRACT_REVIEW_ACTIONS,
  RFX_EVALUATION_ACTIONS,
  CLAUSE_CHECK_ACTIONS,
  VMO_BOT_ACTIONS,
} from "../../modules/vscDashboard/store/dashboard.thunk";
import { handleDriveUploadCommonMethod } from "../../common/utils";
import Typography from "../Typography/Typography";
import { Dialog } from "@mui/material";
import { ErrorCode, useDropzone } from "react-dropzone";
import UploadIconOutlined from "../../assets/upload_grey.svg";
import { handleValidationForDashboardEntity } from "../../modules/vscDashboard/utils";
import BrowseFilesSvg from "../../assets/browse_files_icon.svg";
import OrionLogo from "../../assets/Orion Logo.svg";
import DocumentStoreModal from "../../modules/vscDashboard/components/DocumentStore/DocumentStoreModal";
import { RFX_GENERATOR_ACTIONS } from "../../modules/vscDashboard/store/dashboard.thunk";
interface UploadDocProps {
  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  postUpload?: () => void;
  dashboardEntity: string;
  onFileUpload?: (file: File) => void; // <-- Accept callback
  callFromChatbot?: boolean; // <-- Optional prop to handle chatbot specific logic
}

const UploadDoc = ({
  openModal,
  setOpenModal,
  postUpload,
  dashboardEntity,
  onFileUpload, // <-- Accept callback
  callFromChatbot, // <-- Optional prop to handle chatbot specific logic
}: UploadDocProps) => {
  const [file, setFile] = useState({} as any);
  const [localFileDetails, setLocalFilesDetails] = useState<any>([]);
  const [isFileUploading, setIsFileuploading] = useState(false);
  const [fileDetails, setFileDetails] = useState({} as FileUploadRequestBody);
  const [isFileFetched, setIsFileFetched] = useState(false);
  const [error, setError] = useState("");
  const [openingPicker, setOpeningPicker] = useState(false);
  const [openDocStore, setOpenDocStore] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [openPicker] = useDrivePicker();
  const dispatch = useAppDispatch();
  const { dropZoneProps, driveMimeTypes } = UploadDocConfig?.[dashboardEntity];
  // const disableDriveupload = [DASHBOARD_ENTITIES.CONTRACT_DRAFTING].includes(dashboardEntity);

  useEffect(() => {
    if (file) {
      onFileUpload?.(file);
    }
  }, [fileDetails, file, onFileUpload]);
  const {
    accept,
    disabled,
    driveMultiSelect,
    uploadWarningText,
    maxFiles,
    multiple,
    maxSize,
    fileSizeError,
  } = dropZoneProps;

  const {
    acceptedFiles,
    getRootProps,
    getInputProps,
    isDragReject,
    isDragAccept,
  } = useDropzone({
    maxFiles: maxFiles,
    multiple: multiple,
    disabled: disabled,
    accept: accept,
    maxSize: maxSize,
    onDrop(acceptedFiles, fileRejections, event) {
      // works for only Contract Redlining, need to introduce changes to make it more generic
      if (fileRejections.length > 0) {
        const errorCode = fileRejections[0].errors[0].code;
        if (errorCode === ErrorCode.FileTooLarge) {
          handleError(
            fileSizeError || FILE_UPLOAD_ERRORS.EXCEEDS_SIZE_CANNOT_EXCEED_10MB
          );
        }
        if (errorCode === ErrorCode.FileInvalidType) {
          handleError(dropZoneProps?.fileTypeError);
        }
      } else {
        if (acceptedFiles.length > 0) {
          if (
            acceptedFiles?.length === 1 &&
            dashboardEntity !== DASHBOARD_ENTITIES.RFX_EVALUATION &&
            dashboardEntity !== DASHBOARD_ENTITIES.CLAUSE_CHECK &&
            dashboardEntity !== DASHBOARD_ENTITIES.TALK_TO_CONTRACT
          ) {
            setFile(acceptedFiles[0]);

            setFileDetails({
              file_name: acceptedFiles[0].name,
              source_file_name: acceptedFiles[0].name,
              content_type: acceptedFiles[0].type,
            });
          } else {
            let totalFileSize = 0;
            acceptedFiles.forEach((file: File) => {
              totalFileSize += file.size;
            });
            if (totalFileSize > maxSize) {
              handleError(fileSizeError);
              return;
            }

            const filesData = acceptedFiles?.map((file: any) => {
              return {
                file: file,
                name: file?.name,
              };
            });
            setLocalFilesDetails(filesData);
          }

          setIsFileFetched(true);
          setError("");
        }
      }
    },
  });

  const { selectedTemplate } = useAppSelector(
    (store) => store?.rootReducer?.vscDashboardReducer
  )?.contractDrafting;

  const { rfxGeneratorSelectedTemplate } = useAppSelector(
    (store) => store?.rootReducer?.vscDashboardReducer
  )?.rfxGenerator;

  const resetFileDetails = () => {
    setFileDetails({
      file_name: "",
      source_file_name: "",
      content_type: "",
    });
  };

  const handleOnClose = () => {
    dispatch(setModalOpen(false));
    setOpenModal(false);
    setFile({});
    setIsFileFetched(false);
    setIsFileuploading(false);
    setError("");
    resetFileDetails();
  };

  const handleError = (reason: string) => {
    setError(reason);
    setFile({});
    setIsFileFetched(false);
    resetFileDetails();
  };

  const handleUpload = async () => {
    setIsFileuploading(true);
    switch (dashboardEntity) {
      case DASHBOARD_ENTITIES.CONTRACT_DRAFTING:
        await dispatch(
          uploadFileForContractDraftingThunk({
            file,
            fileDetails,
            isLocal: true,
            selectedTemplate: selectedTemplate,
            callFromChabot: callFromChatbot,
          })
        );
        break;

      case DASHBOARD_ENTITIES.CONTRACT_REVIEW:
        await dispatch(
          uploadFileForContractReviewThunk({ file, fileDetails, isLocal: true })
        );

        break;

      case DASHBOARD_ENTITIES.RFX_EVALUATION:
        await dispatch(
          uploadFilesForRfxEvaluationThunk({
            filesFromLocal: localFileDetails,
            isLocal: true,
          })
        );
        break;
      case DASHBOARD_ENTITIES.RFX_GENERATOR:
        await dispatch(
          uploadFileForRfxGeneratorThunk({
            file,
            fileDetails,
            isLocal: true,
            rfxGeneratorSelectedTemplate: rfxGeneratorSelectedTemplate,
          })
        );
        break;
      case DASHBOARD_ENTITIES.CLAUSE_CHECK:
        await dispatch(
          uploadFileForClauseCheckThunk({
            docs: localFileDetails,
            isLocal: true,
          })
        );
        break;
      case DASHBOARD_ENTITIES.TALK_TO_CONTRACT:
        await dispatch(
          uploadMutliDocFromBotThunk({
            docs: localFileDetails,
            isLocal: true,
          })
        );
        break;
      default:
        break;
    }
    setIsFileuploading(false);
    handleOnClose();
    postUpload && postUpload();
  };

  const handleFileUploadFromDrive = async (data: any) => {
    const doc = data?.docs?.[0];
    setIsFileuploading(true);

    let commonProps = {
      file: {} as File,
      isLocal: false,
    };

    switch (dashboardEntity) {
      case DASHBOARD_ENTITIES.CLAUSE_CHECK:
        await dispatch(
          uploadFileForClauseCheckThunk({
            docs: data?.docs,
            isLocal: false,
          })
        );
        break;
      case DASHBOARD_ENTITIES.CONTRACT_DRAFTING:
        const contractDraftingRequestBody = {
          fileDetails: {
            file_name: doc?.name,
            id: doc?.id,
          },
          isLocal: false,
          selectedTemplate: { id: selectedTemplate?.id },
          callFromChabot: callFromChatbot,
        };
        await dispatch(
          uploadFileForContractDraftingThunk({
            ...contractDraftingRequestBody,
            file: {} as File,
          })
        );
        break;
      case DASHBOARD_ENTITIES.CONTRACT_REVIEW:
        const contractReviewRequestBody = {
          ...commonProps,
          fileDetails: { file_name: doc?.name + ".docx", id: doc?.id },
        };
        await dispatch(
          uploadFileForContractReviewThunk(contractReviewRequestBody)
        );
        break;

      case DASHBOARD_ENTITIES.RFX_EVALUATION:
        await dispatch(
          uploadFilesForRfxEvaluationThunk({
            docsFromDrive: data?.docs,
            filesFromLocal: [
              {
                file: {} as File,
                name: "",
              },
            ],
            isLocal: false,
          })
        );
        break;
      case DASHBOARD_ENTITIES.RFX_GENERATOR:
        const thunkBody = {
          docsFromDrive: data?.docs,
          file: {} as File,
          fileDetails: { file_name: doc?.name, id: doc?.id },
          isLocal: false,
          rfxGeneratorSelectedTemplate: rfxGeneratorSelectedTemplate,
        };
        await dispatch(uploadFileForRfxGeneratorThunk(thunkBody));
        break;
      case DASHBOARD_ENTITIES.TALK_TO_CONTRACT:
        const all_doc = data?.docs;
        await dispatch(
          uploadMutliDocFromBotThunk({
            docs: all_doc,
            isLocal: false,
          })
        );
        break;
      default:
        break;
    }

    setIsFileuploading(false);
    handleOnClose();
    postUpload && postUpload();
  };

  const handleCallBackResponse = (data: any) => {
    const { action } = data;
    switch (action) {
      case "picked":
        let errorMessage = handleValidationForDashboardEntity({
          data: data,
          entity: dashboardEntity,
        });

        if (errorMessage) {
          return handleError(errorMessage);
        }
        // set when no issue and validations passed
        setIsFileFetched(true);
        setError("");
        handleFileUploadFromDrive(data);

        break;

      case "cancel":
      default:
        break;
    }
  };

  const handleDriveUpload = async (e: any) => {
    e.stopPropagation();
    await handleDriveUploadCommonMethod({
      setLoader: setOpeningPicker,
      openPicker: openPicker,
      multiselect: driveMultiSelect,
      mimeTypes: driveMimeTypes,
      onResponse: (data: any) => {
        handleCallBackResponse(data);
      },
    });
  };

  const showDocStore = (e: any) => {
    e.stopPropagation();
    switch (dashboardEntity) {
      case DASHBOARD_ENTITIES.RFX_GENERATOR:
        dispatch({
          type: RFX_GENERATOR_ACTIONS.UPDATE_RFX_GENERATOR_DATA,
          payload: { fileUpload: { error: {} } },
        });
        break;
      case DASHBOARD_ENTITIES.CONTRACT_DRAFTING:
        dispatch({
          type: CONTRACT_DRAFTING_ACTIONS.UPDATE_CONTRACT_DRAFTING_DATA,
          payload: { fileUpload: { error: {} } },
        });
        break;
      case DASHBOARD_ENTITIES.CONTRACT_REVIEW:
        dispatch({
          type: CONTRACT_REVIEW_ACTIONS.UPDATE_FILE_UPLOAD_STATUS,
          payload: { isUploading: false, error: {}, uploaded: false },
        });
        break;
      case DASHBOARD_ENTITIES.RFX_EVALUATION:
        dispatch({
          type: RFX_EVALUATION_ACTIONS.UPDATE_RFX_EVALUATION_DATA,
          payload: { docsUploading: false, error: {}, docsUploaded: false },
        });
        break;
      case DASHBOARD_ENTITIES.CLAUSE_CHECK:
        dispatch({
          type: CLAUSE_CHECK_ACTIONS.SET_CLAUSE_CHECK_DATA,
          payload: { error: {}, uploadProcessCompleted: false },
        });
        break;
      case DASHBOARD_ENTITIES.TALK_TO_CONTRACT:
        dispatch({
          type: VMO_BOT_ACTIONS.UPDATE_VMO_BOT_DATA,
          payload: {
            isUploading: false,
            uploadProcessCompleted: false,
            error: {},
          },
        });
        break;
      default:
        break;
    }
    setOpenDocStore(true);
  };
  const onClick = (e: any) => {
    e.stopPropagation();
    const props = getInputProps();
    if (props) props.onClick && props.onClick(e);
  };

  const handleCloseClick = () => {
    setOpenDocStore(false);
  };

  const handleCancelClick = () => {
    dispatch(setModalOpen(false));
    setOpenDocStore(false);
    handleOnClose();
    setIsFileuploading(false);
    handleOnClose();
    postUpload && postUpload();
  };

  const stylesForCurrentState = isFileUploading
    ? UploadDocStyles.disabled
    : (error && error?.length > 0) || isDragReject
    ? UploadDocStyles.error
    : acceptedFiles?.length > 0 || isDragAccept
    ? UploadDocStyles.success
    : UploadDocStyles.idle;

  return (
    <Fragment>
      <Dialog
        open={openModal}
        onClose={handleOnClose}
        sx={{
          zIndex: 1000,
        }}
      >
        <div className={UploadDocStyles.uploadDocContainer}>
          <div className={UploadDocStyles.uploadDocHeader}>
            <Typography
              fontWeight={700}
              fontSize={17}
              lineHeight="20px"
              color="var(--tertiaryDarkestBlueColor)"
            >
              Upload Documents
            </Typography>
            <img src={CloseIcon} alt="" onClick={handleOnClose} color="red" />
          </div>
          <div className={UploadDocStyles.uploadDocumentBody}>
            <div
              {...getRootProps({
                className: `${UploadDocStyles.uploadDocumentDragandDrop} ${stylesForCurrentState}`,
              })}
            >
              <input {...getInputProps()} disabled={isFileUploading} />

              {/* {isClauseCheck ? (
                <ChooseFromDriveBtn
                  onClick={handleDriveUpload}
                  loading={openingPicker}
                  disabled={isFileUploading}
                />
              ) : ( */}
              <Fragment>
                <img src={BrowseFileSvg} alt="" />
                <Typography color="var(--tertiaryDarkestBlueColor)">
                  Drag and Drop Files or Folders here
                </Typography>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    width: "-webkit-fill-available",
                  }}
                >
                  {
                    <Button
                      onClick={showDocStore}
                      variant="outlined"
                      disabled={isFileUploading}
                      endIcon={<img src={OrionLogo} alt="" />}
                      style={{ padding: "8px 8px" }}
                    >
                      <Typography color="var(--primaryBlueColor)">
                        Upload From
                      </Typography>
                    </Button>
                  }
                  <Button
                    variant="outlined"
                    disabled={isFileUploading}
                    endIcon={<img src={BrowseFilesSvg} alt="" />}
                    style={{ padding: "8px 8px" }}
                  >
                    <Typography color="var(--primaryBlueColor)">
                      Browse Files
                    </Typography>
                  </Button>
                  {
                    // {!isClauseCheck && !disableDriveupload && (
                    <ChooseFromDriveBtn
                      onClick={handleDriveUpload}
                      loading={openingPicker}
                      disabled={isFileUploading}
                      style={{ padding: "8px 8px" }}
                    />
                  }
                </div>
                {openDocStore && (
                  <DocumentStoreModal
                    open={openDocStore}
                    onClose={handleCloseClick}
                    onCancel={handleCancelClick}
                    dashboardEntity={dashboardEntity}
                  />
                )}
              </Fragment>
            </div>

            <div className={UploadDocStyles.fileSizeTextWarning}>
              <img src={WarningSvg} alt="" />
              <span>{uploadWarningText}</span>
            </div>

            <div className={UploadDocStyles.footerContainer}>
              <Button variant="outlined" onClick={handleOnClose}>
                <Typography fontSize="12px">Cancel</Typography>
              </Button>

              {error && (
                <div className={UploadDocStyles.fileSizeTextError}>
                  <p>{error}</p>
                </div>
              )}

              <div className={UploadDocStyles.uploadBtnContainer}>
                <UploadBtn
                  onClick={handleUpload}
                  disabled={!isFileFetched || isFileUploading}
                  loading={isFileUploading}
                  startIcon={
                    isFileUploading && <img src={UploadIconOutlined} alt="" />
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </Fragment>
  );
};

export default UploadDoc;
